import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

// Deep clone function for nodes
const cloneNode = (n, newTag = null, reset = false) => {
  return {
    tag: newTag || n.tag,
    text: reset ? "" : n.text,
    children: n.children
      ? n.children.map((c) => cloneNode(c, null, reset))
      : [],
  };
};

// Map for node references (kept outside the component)
const nodeRefs = {};

// Helper to truncate text for TOC display and context passing
const truncateText = (text, maxLength = 50) => {
  if (!text) return "";
  text = text.trim().replace(/[\n\r]/g, " "); // Remove newlines
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
};

// List of all tags that should be rendered as a simple text input field
const textEditableTags = [
  "Description",
  "Name",
  "OtherInformation",
  "SequenceIndicator",
  "StartDate",
  "EndDate",
  "PublicationDate",
  "Source",
  "GivenName",
  "Surname",
  "PhoneNumber",
  "EmailAddress",
  "Acronym",
  "Identifier",
  "Role",
  "FirstName",
  "LastName",
  "DescriptorName",
  "DescriptorValue",
  "MeasurementDimension",
  "UnitOfMeasurement",
  "ReferentIdentifier",
];

// Map for main tags (for editor component logic)
const mainTags = [
  "Vision",
  "Mission",
  "Value",
  "Goal",
  "Objective",
  "Submitter",
  "Organization",
  "Stakeholder",
  "AdministrativeInformation",
];

// **FINAL TOC STRUCTURE HELPER - Uses XML content for display names**
const getTocStructure = (node, path = [], results = []) => {
  if (!node || !node.tag) return [];

  const nodeId = path.join("-");
  node.id = nodeId || "root";

  let currentEntry = null;

  // 1. Handle Vision and Mission
  if (node.tag === "Vision" || node.tag === "Mission") {
    const display = node.tag;
    currentEntry = { tag: node.tag, display, id: node.id, children: [] };
    results.push(currentEntry);
  }
  // 2. Handle AdministrativeInformation
  else if (node.tag === "AdministrativeInformation") {
    currentEntry = { tag: node.tag, display: node.tag, id: node.id, children: [] };

    // Find and process children with values
    const adminChildren = node.children.filter((c) =>
      ["PublicationDate", "Source"].includes(c.tag)
    );
    adminChildren.forEach((child, i) => {
      const valPath = [...path, `${child.tag}-${i}`];
      const display = `${child.tag}: ${child.text || "N/A"}`;
      const valEntry = {
        tag: child.tag,
        display: display,
        id: valPath.join("-"),
        children: [],
      };
      currentEntry.children.push(valEntry);
    });
    results.push(currentEntry);
  }
  // 3. Handle Goals
  else if (node.tag === "Goal") {
    const nameNode = node.children.find((c) => c.tag === "Name");
    const seqNode = node.children.find((c) => c.tag === "SequenceIndicator");

    const sequence = seqNode ? seqNode.text : "";
    const name = nameNode ? nameNode.text.replace(/&amp;/g, "&") : "";
    const display = sequence && name ? `${sequence}: ${name}` : name || node.tag;

    currentEntry = { tag: node.tag, display, id: node.id, children: [] };

    // Find and process Objectives (children of Goal)
    node.children
      .filter((c) => c.tag === "Objective")
      .forEach((obj, i) => {
        const objPath = [...path, `Objective-${i}`];
        const objEntry = getTocStructure(obj, objPath);
        if (objEntry.length > 0) {
          currentEntry.children.push(objEntry[0]);
        }
      });
    results.push(currentEntry);
  }
  // 4. Handle Objectives (children of Goal)
  else if (node.tag === "Objective") {
    const nameNode = node.children.find((c) => c.tag === "Name");
    const seqNode = node.children.find((c) => c.tag === "SequenceIndicator");

    const sequence = seqNode ? seqNode.text : "";
    const name = nameNode ? nameNode.text : "";
    const display = sequence && name ? `${sequence}: ${name}` : name || node.tag;

    currentEntry = { tag: node.tag, display, id: node.id, children: [] };
    results.push(currentEntry);
  }
  // 5. Handle StrategicPlanCore (to group Values and Goals/Vision/Mission)
  else if (node.tag === "StrategicPlanCore") {
    const valueNodes = node.children.filter((c) => c.tag === "Value");

    if (valueNodes.length > 0) {
      const valuesContainer = {
        tag: "Values_Container",
        display: "Values",
        id: `${node.id}-Values`,
        children: [],
      };

      valueNodes.forEach((val, i) => {
        const valPath = [...path, `Value-${i}`];
        const nameNode = val.children.find((c) => c.tag === "Name");
        const display = nameNode ? nameNode.text : "Value";
        const valEntry = {
          tag: "Value",
          display: display,
          id: valPath.join("-"),
          children: [],
        };
        valuesContainer.children.push(valEntry);
      });
      results.push(valuesContainer);
    }

    const coreItems = node.children.filter((c) =>
      ["Vision", "Mission", "Goal"].includes(c.tag)
    );
    coreItems.forEach((child, index) => {
      const childPath = [...path, `${child.tag}-${index}`];
      getTocStructure(child, childPath, results);
    });
  }
  // 6. Traverse the rest
  else if (node.children) {
    node.children.forEach((child, index) => {
      const childPath = [...path, `${child.tag}-${index}`];
      getTocStructure(child, childPath, results);
    });
  }

  if (path.length === 0) {
    const finalResults = [];

    node.children.forEach((child, index) => {
      const childPath = [`root`, `${child.tag}-${index}`];
      if (child.tag === "StrategicPlanCore") {
        getTocStructure(child, childPath, finalResults);
      } else if (child.tag === "AdministrativeInformation") {
        getTocStructure(child, childPath, finalResults);
      } else if (["Name", "Description", "OtherInformation"].includes(child.tag)) {
        // Ignore top-level text nodes in TOC
      } else if (["Organization"].includes(child.tag)) {
        const orgNameNode = child.children.find((c) => c.tag === "Name");
        if (orgNameNode) {
          finalResults.push({
            tag: child.tag,
            display: `Organization: ${orgNameNode.text}`,
            id: childPath.join("-"),
            children: [],
          });
        }
      }
    });
    return finalResults;
  }

  return [currentEntry];
};

// **Recursive TocItem Component**
const TocItem = ({
  item,
  scrollToNode,
  tocCollapsedState,
  setTocCollapsedState,
}) => {
  const isCollapsed = tocCollapsedState[item.id] === true;

  const toggleCollapse = (e) => {
    e.stopPropagation();
    setTocCollapsedState((prev) => ({
      ...prev,
      [item.id]: !prev[item.id],
    }));
  };

  const hasChildren = item.children && item.children.length > 0;

  let level = 1;
  if (
    item.tag === "Value" ||
    item.tag === "Objective" ||
    item.tag === "PublicationDate" ||
    item.tag === "Source"
  )
    level = 2;

  const indentStyle = {
    marginLeft: `${(level - 1) * 15}px`,
    fontWeight: level === 1 && hasChildren ? "600" : "400",
    fontSize: level === 1 ? "1rem" : "0.875rem",
    paddingLeft: hasChildren ? "0px" : "15px",
  };

  return (
    <li className="list-none w-full">
      <div
        className={`flex items-center gap-1 py-0.5 cursor-pointer w-full text-blue-600 dark:text-blue-400 hover:underline transition-colors ${
          level > 1 ? "text-gray-700 dark:text-gray-300" : ""
        }`}
        style={indentStyle}
        onClick={() => scrollToNode(item.id)}
        title={item.display}
      >
        {hasChildren && (
          <span
            onClick={toggleCollapse}
            className="text-xs text-blue-500 dark:text-blue-300 mr-1"
          >
            {isCollapsed ? "▶" : "▼"}
          </span>
        )}
        <span
          className="truncate flex-1"
          style={{ fontWeight: level === 1 ? "500" : "normal" }}
        >
          {item.display}
        </span>
      </div>

      {!isCollapsed && hasChildren && (
        <ul className="pl-1 mt-1 space-y-0.5">
          {item.children.map((child, index) => (
            <TocItem
              key={child.id || index}
              item={child}
              scrollToNode={scrollToNode}
              tocCollapsedState={tocCollapsedState}
              setTocCollapsedState={setTocCollapsedState}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

// XmlNode component
const XmlNode = ({
  node,
  level = 0,
  onUpdate,
  parent = null,
  index = null,
  organizationName = null,
  ancestorContext = [],
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [loadingAI, setLoadingAI] = useState(false);
  const [previousText, setPreviousText] = useState(node.text || "");

  const nodeRef = useRef(null);

  const nodeId =
    node.id || (parent ? (parent.id || "root") + `-${node.tag}-${index}` : "root");

  useEffect(() => {
    if (nodeRef.current) {
      nodeRefs[nodeId] = nodeRef.current;
    }
    return () => {
      delete nodeRefs[nodeId];
    };
  }, [nodeId]);

  const updateNodeText = (text) => {
    setPreviousText(node.text || "");
    node.text = text;
    onUpdate();
  };

  const revertText = () => {
    node.text = previousText;
    onUpdate();
  };

  const deleteNode = () => {
    if (parent && index !== null) {
      parent.children.splice(index, 1);
      onUpdate();
    }
  };

  const addChildElement = (childTag) => {
    const newNode = { tag: childTag, text: "", children: [] };

    if (childTag === "Stakeholder") {
      newNode.children = [
        { tag: "Name", text: "", children: [] },
        { tag: "Role", text: "", children: [] },
      ];
    } else if (childTag === "Goal" || childTag === "Objective") {
      newNode.children = [
        { tag: "Name", text: `New ${childTag}`, children: [] },
        { tag: "SequenceIndicator", text: "X.X", children: [] },
        { tag: "Description", text: "", children: [] },
        {
          tag: "Stakeholder",
          children: [
            { tag: "Name", text: "", children: [] },
            { tag: "Description", text: "", children: [] },
          ],
        },
        { tag: "OtherInformation", text: "", children: [] },
      ];
    } else if (childTag === "Value") {
      newNode.children = [
        { tag: "Name", text: "New Value", children: [] },
        { tag: "Description", text: "", children: [] },
      ];
    }

    if (!node.children) node.children = [];

    let insertIndex = node.children.length;
    for (let i = node.children.length - 1; i >= 0; i--) {
      if (node.children[i].tag === childTag) {
        insertIndex = i + 1;
        break;
      }
    }

    node.children.splice(insertIndex, 0, newNode);
    setCollapsed(false);
    onUpdate();
  };

  const addSiblingNode = () => {
    if (!parent || index === null) return;
    const newNode = cloneNode(node, node.tag, true);
    parent.children.splice(index + 1, 0, newNode);
    onUpdate();
  };

  const addChildGoal = () => addChildElement("Goal");
  const addChildObjective = () => addChildElement("Objective");
  const addChildValue = () => addChildElement("Value");

  const isMain = mainTags.includes(node.tag);
  const isTextNode = textEditableTags.includes(node.tag);

  const generateAIText = async () => {
    if (!parent) return;
    setLoadingAI(true);

    let contextParts = [];
    const currentText = node.text?.trim() || "";
    let instruction = "";

    if (currentText) {
      contextParts.push(`CURRENT TEXT TO MODIFY: "${currentText}"`);
      instruction = `Please read the CURRENT TEXT TO MODIFY provided. Based on the Node Tag and Hierarchy/Context, MODIFY and improve the current text. Do not start with phrases like 'The modified text is'. Return only the new, modified text.`;
    } else {
      instruction = `Based on the Node Tag and Hierarchy/Context, GENERATE a high-quality, professional description for this field. Return only the generated text.`;
    }

    contextParts.push(`INSTRUCTION: ${instruction}`);

    if (organizationName) {
      contextParts.push(`Organization: ${organizationName}`);
    }

    if (ancestorContext.length > 0) {
      contextParts.push("Hierarchy/Context: " + ancestorContext.join(" -> "));
    }

    let siblingName = "";
    let siblingSeq = "";
    if (parent.children) {
      const nameNode = parent.children.find((c) => c.tag === "Name");
      const seqNode = parent.children.find((c) => c.tag === "SequenceIndicator");
      siblingName = nameNode?.text?.trim() || "";
      siblingSeq = seqNode?.text?.trim() || "";
    }

    if (siblingSeq || siblingName) {
      contextParts.push(`RELATED ITEM NAME: ${siblingSeq}${siblingSeq ? ": " : ""}${siblingName}`);
    }

    const fullContext = contextParts.join(". ");

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/generate-node`, {
        node_tag: node.tag,
        context: fullContext.trim() || "General context for a Strategic Plan element",
      });

      if (res.data.generated_text) {
        updateNodeText(res.data.generated_text);
      } else if (res.data.error) {
        console.error("AI Generation Error:", res.data.error);
      }
    } catch (err) {
      console.error("Network Error during AI generation:", err);
    } finally {
      setLoadingAI(false);
    }
  };

  const displayTitleTags = [
    "Goal",
    "Objective",
    "Value",
    "Vision",
    "Mission",
    "Organization",
    "Stakeholder",
  ];
  let displayName = node.tag;

  if (displayTitleTags.includes(node.tag) && node.children) {
    const nameNode = node.children.find((c) => c.tag === "Name");
    const nameText = nameNode?.text?.trim().replace(/&amp;/g, "&");
    if (nameText) {
      displayName = `${node.tag}: ${nameText}`;
    }
  }

  // Define newAncestorContext
  const newAncestorContext = [...ancestorContext];

  if (["Vision", "Mission", "Goal", "Objective"].includes(node.tag)) {
    const nameNode = node.children.find((c) => c.tag === "Name");
    const descriptionNode = node.children.find((c) => c.tag === "Description");

    let context = `${node.tag}`;
    if (nameNode?.text) {
      context += ` named "${nameNode.text.replace(/&amp;/g, "&")}"`;
    }
    if (descriptionNode?.text) {
      context += ` with a description: "${truncateText(descriptionNode.text, 50)}"`;
    }
    newAncestorContext.push(context);
  }

  const isMultiLineText = node.tag === "Description" || node.tag === "OtherInformation";
  const canDelete = parent && mainTags.includes(node.tag);

  return (
    <div ref={nodeRef} id={nodeId} style={{ marginLeft: level * 20 }} className="mb-3">
      <div className="mb-2 bg-gray-50 dark:bg-slate-700 p-3 rounded flex flex-col gap-2 border border-gray-300 dark:border-slate-600 overflow-x-auto">
        <div className="flex items-center gap-2 flex-wrap w-full min-w-max">
          {node.children && node.children.length > 0 && (
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="text-sm text-gray-500 dark:text-slate-300"
            >
              {collapsed ? "▶" : "▼"}
            </button>
          )}
          <h3
            className="font-semibold text-lg text-gray-800 dark:text-slate-200"
            title={displayName}
          >
            {displayName}
          </h3>

          <div className="ml-auto flex gap-2 flex-wrap justify-end">
            {node.tag === "StrategicPlanCore" && (
              <button
                onClick={addChildGoal}
                className="bg-purple-500 text-white text-sm px-2 py-1 rounded hover:bg-purple-600"
              >
                ➕ Add Goal
              </button>
            )}

            {node.tag === "Goal" && (
              <button
                onClick={addChildObjective}
                className="bg-orange-500 text-white text-sm px-2 py-1 rounded hover:bg-orange-600"
              >
                ➕ Add Objective
              </button>
            )}

            {node.tag === "StrategicPlanCore" && (
              <button
                onClick={addChildValue}
                className="bg-teal-500 text-white text-sm px-2 py-1 rounded hover:bg-teal-600"
              >
                ➕ Add Value
              </button>
            )}

            {parent && node.tag === "Source" && (
              <button
                onClick={addSiblingNode}
                className="bg-green-500 text-white text-sm px-2 py-1 rounded hover:bg-green-600"
              >
                ➕ Add {node.tag}
              </button>
            )}

            {isMain && parent && (
              <button
                onClick={addSiblingNode}
                className="bg-green-500 text-white text-sm px-2 py-1 rounded hover:bg-green-600"
              >
                ➕ Add {node.tag}
              </button>
            )}

            {canDelete && (
              <button
                onClick={deleteNode}
                className="bg-red-500 text-white text-sm px-2 py-1 rounded hover:bg-red-600"
              >
                ❌ Delete
              </button>
            )}
          </div>
        </div>

        {isTextNode && (
          <div className="flex gap-2 items-start">
            {isMultiLineText ? (
              <textarea
                value={node.text || ""}
                onChange={(e) => updateNodeText(e.target.value)}
                className="border-2 border-gray-300 dark:border-gray-600 rounded px-2 py-1 w-full text-black dark:text-slate-100 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                rows={4}
              />
            ) : (
              <input
                type="text"
                value={node.text || ""}
                onChange={(e) => updateNodeText(e.target.value)}
                className="border-2 border-gray-300 dark:border-gray-600 rounded px-2 py-1 w-full text-black dark:text-slate-100 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            )}

            {(node.tag === "Description" || node.tag === "OtherInformation") && (
              <>
                {previousText !== (node.text || "") && (
                  <button
                    onClick={revertText}
                    className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 text-sm h-fit mt-1"
                    title="Revert to previous text"
                  >
                    ⟲
                  </button>
                )}
                <button
                  onClick={generateAIText}
                  disabled={loadingAI}
                  className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600 text-sm h-fit mt-1"
                >
                  {loadingAI ? "Generating..." : "AI"}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {!collapsed &&
        node.children &&
        node.children.map((c, i) => (
          <XmlNode
            key={`${c.tag}-${i}-${nodeId}`}
            node={c}
            level={level + 1}
            onUpdate={onUpdate}
            parent={node}
            index={i}
            organizationName={organizationName}
            ancestorContext={newAncestorContext}
          />
        ))}
    </div>
  );
};

// Extract Organization Name
const extractOrganizationName = (data) => {
  if (!data || !data.children) return null;
  const orgNode = data.children.find((c) => c.tag === "Organization");
  if (orgNode && orgNode.children) {
    const nameNode = orgNode.children.find((c) => c.tag === "Name");
    return nameNode ? nameNode.text : null;
  }
  return null;
};

// Convert xmlData to formatted XML string
const generateXmlString = (node, level = 0) => {
  if (!node) return "";

  const indent = "  ".repeat(level);
  let xml = "";

  xml += `${indent}<${node.tag}`;
  if (node.id) {
    xml += ` id="${node.id}"`;
  }
  if (!node.text && (!node.children || node.children.length === 0)) {
    xml += "/>\n";
    return xml;
  }
  xml += ">";

  if (node.text) {
    const escapedText = node.text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;");
    xml += escapedText;
  } else {
    xml += "\n";
  }

  if (node.children && node.children.length > 0) {
    node.children.forEach((child) => {
      xml += generateXmlString(child, level + 1);
    });
  }

  if (node.text || (node.children && node.children.length > 0)) {
    xml += `${indent}</${node.tag}>\n`;
  }

  return xml;
};

export default function ExmlEditor() {
  const [file, setFile] = useState(null);
  const [xmlData, setXmlData] = useState(null);
  const [uploadedFilename, setUploadedFilename] = useState(null);
  const [importUrl, setImportUrl] = useState("");
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [tocStructure, setTocStructure] = useState(null);
  const [tocCollapsedState, setTocCollapsedState] = useState({});
  const [organizationName, setOrganizationName] = useState(null);

  // Define handleFileChange
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  useEffect(() => {
    theme === "dark"
      ? document.documentElement.classList.add("dark")
      : document.documentElement.classList.remove("dark");
  }, [theme]);

  useEffect(() => {
    if (xmlData) {
      setTocStructure(getTocStructure(xmlData));
      setOrganizationName(extractOrganizationName(xmlData));
    } else {
      setTocStructure(null);
      setOrganizationName(null);
    }
  }, [xmlData]);

  const collapseAllToc = () => {
    const allIds = {};
    const traverse = (items) => {
      items.forEach((item) => {
        if (item && item.id) {
          allIds[item.id] = true;
        }
        if (item && item.children) {
          traverse(item.children);
        }
      });
    };
    if (tocStructure) {
      traverse(tocStructure);
      setTocCollapsedState(allIds);
    }
  };

  const expandAllToc = () => {
    const allIds = {};
    const traverse = (items) => {
      items.forEach((item) => {
        if (item && item.id) {
          allIds[item.id] = false;
        }
        if (item && item.children) {
          traverse(item.children);
        }
      });
    };
    if (tocStructure) {
      traverse(tocStructure);
      setTocCollapsedState(allIds);
    }
  };

  const toggleCollapseAll = () => {
    const allCollapsed =
      tocStructure &&
      Object.keys(tocCollapsedState).length > 0 &&
      Object.values(tocCollapsedState).every((val) => val === true);
    if (allCollapsed) {
      expandAllToc();
    } else {
      collapseAllToc();
    }
  };

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchXml = async (filename) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/read-xml/${filename}`
      );
      setXmlData(res.data);
      showToast("File loaded successfully!", "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to read XML. Check server connection or file format.", "error");
    }
  };

  const uploadFile = async () => {
    if (!file) return showToast("Select a file!", "error");
    const form = new FormData();
    form.append("file", file);
    setIsLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/upload`, form);
      const path = res.data.filename.replace(/\\/g, "/");
      const filename = path.split("/").pop(); // Extract only the filename
      setUploadedFilename(filename);
      await fetchXml(filename);
    } catch (err) {
      console.error(err);
      showToast("File upload failed. Check server configuration.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchXmlFromUrl = async () => {
    if (!importUrl) return showToast("Enter a URL!", "error");
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/read-xml-url?url=${encodeURIComponent(
          importUrl
        )}`
      );
      setXmlData(res.data);
      const filename = importUrl.split("/").pop();
      await axios.post(`${process.env.REACT_APP_API_URL}/upload-url`, {
        url: importUrl,
        filename,
      });
      setUploadedFilename(filename);
      showToast("Imported from URL successfully!", "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to import from URL. Check the URL and server.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const saveXml = async () => {
    if (!xmlData || !uploadedFilename)
      return showToast("Nothing to save! Load or import a file first.", "error");
    setIsLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/save-xml/${uploadedFilename}`,
        xmlData
      );
      showToast("Saved successfully!", "success");
    } catch (err) {
      console.error(err);
      showToast("Save failed. Check server connection.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const downloadXml = async () => {
    if (!uploadedFilename)
      return showToast("Nothing to download! Load or import a file first.", "error");
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/download-xml/${uploadedFilename}`,
        {
          responseType: "blob",
        }
      );
      const blob = new Blob([res.data], { type: "application/xml" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const nameWithoutExt = uploadedFilename.split("/").pop().replace(/\.xml$/, "");
      const timestamp = new Date().toISOString().replace(/[:.-]/g, "_").slice(0, 19);
      link.download = `${nameWithoutExt}_${timestamp}.xml`;
      link.click();
      URL.revokeObjectURL(link.href);
      showToast("Downloaded successfully!", "success");
    } catch (err) {
      console.error(err);
      showToast("Download failed. Check server connection.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToNode = (nodeId) => {
    const element = nodeRefs[nodeId] || document.getElementById(nodeId);
    if (element) {
      const editorOffsetTop = element.getBoundingClientRect().top + window.scrollY;
      window.scroll({
        top: editorOffsetTop - 50,
        behavior: "smooth",
      });
    } else {
      showToast(`Node reference for ID "${nodeId}" not found.`, "error");
    }
  };

  const handlePreviewXml = () => {
    if (!xmlData || !uploadedFilename) {
      showToast("No XML data or filename to preview! Load or import a file first.", "error");
      return;
    }
    const previewUrl = `${process.env.REACT_APP_PREVIEW_BASE_URL}/${encodeURIComponent(uploadedFilename)}`;
    window.open(previewUrl, "_blank");
  };

  return (
    <div className={`font-sans min-h-screen p-0 ${theme === "dark" ? "dark" : ""}`}>
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-700 p-6 rounded-lg shadow-2xl flex flex-col items-center">
            <svg
              className="animate-spin h-8 w-8 text-blue-500 mb-3"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p className="text-gray-800 dark:text-slate-200 font-medium">Processing Request...</p>
          </div>
        </div>
      )}

      {toast && (
        <div
          className={`fixed bottom-5 right-5 px-4 py-2 rounded shadow-lg z-50 ${
            toast.type === "success" ? "bg-green-500 text-white" : "bg-red-500 text-white"
          }`}
        >
          {toast.msg}
        </div>
      )}

      <div className="flex bg-gray-100 dark:bg-slate-900 text-black dark:text-slate-100 min-h-screen">
        {xmlData && tocStructure && (
          <div className="w-64 p-4 border-r border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 sticky top-0 h-screen overflow-y-auto hidden md:block">
            <h2 className="text-xl font-bold mb-4 border-b pb-2 text-center">
              Table of Contents
            </h2>
            <button
              onClick={toggleCollapseAll}
              className="w-full mb-3 py-1 bg-gray-100 dark:bg-slate-700 text-blue-600 dark:text-blue-400 text-sm rounded border border-gray-300 dark:border-slate-600 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
            >
              {tocStructure &&
              Object.keys(tocCollapsedState).length > 0 &&
              Object.values(tocCollapsedState).every((val) => val === true)
                ? "▲ Expand All"
                : "▼ Collapse All"}
            </button>
            <ul className="space-y-0.5">
              {tocStructure.map((item, index) => (
                <TocItem
                  key={item.id || index}
                  item={item}
                  scrollToNode={scrollToNode}
                  tocCollapsedState={tocCollapsedState}
                  setTocCollapsedState={setTocCollapsedState}
                />
              ))}
            </ul>
          </div>
        )}

        <div className={`flex-1 p-6 space-y-6 ${xmlData ? "" : "mx-auto max-w-4xl"}`}>
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold">StratML Editor</h1>
            <button
              onClick={() => {
                const next = theme === "dark" ? "light" : "dark";
                setTheme(next);
                localStorage.setItem("theme", next);
              }}
              className="px-3 py-1 rounded border bg-slate-200 dark:bg-slate-700 border-gray-300 dark:border-slate-600"
            >
              {theme === "dark" ? "Light" : "Dark"} Mode
            </button>
          </div>

          <div className="bg-white dark:bg-slate-800 p-4 rounded shadow-sm border border-gray-200 dark:border-gray-600">
            <h2 className="text-xl font-semibold mb-2">Upload File</h2>
            <div className="flex items-center gap-2 flex-wrap mt-2">
              <input
                type="file"
                onChange={handleFileChange}
                className="border-2 border-gray-400 rounded px-2 py-1 text-black dark:text-slate-100 bg-white dark:bg-slate-700"
              />
              <button
                onClick={uploadFile}
                disabled={isLoading}
                className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition-colors"
              >
                Upload & Read
              </button>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 p-4 rounded shadow-sm border border-gray-200 dark:border-gray-600">
            <h2 className="text-xl font-semibold mb-2">Import from URL</h2>
            <div className="flex items-center gap-2 mt-2">
              <input
                type="text"
                placeholder="https://example.com/file.xml"
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                className="border-2 border-gray-400 rounded px-2 py-1 text-black dark:text-slate-100 bg-white dark:bg-slate-700 w-full md:w-100"
              />
              <button
                onClick={fetchXmlFromUrl}
                disabled={isLoading}
                className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition-colors"
                style={{ width: 150 }}
              >
                Import & Read
              </button>
            </div>
          </div>

          {!xmlData && (
            <div className="bg-white dark:bg-slate-800 p-10 rounded shadow-lg border-2 border-dashed border-gray-400 dark:border-slate-600 text-center">
              <p className="text-xl font-medium text-gray-700 dark:text-slate-300">
                Load an XML file using the **Upload** or **Import from URL** options above to begin
                editing.
              </p>
            </div>
          )}

          {xmlData && (
            <div className="bg-white dark:bg-slate-800 p-4 rounded shadow-sm border border-gray-200 dark:border-gray-600">
              <XmlNode
                node={xmlData}
                onUpdate={() => setXmlData({ ...xmlData })}
                organizationName={organizationName}
                ancestorContext={[]}
              />
            </div>
          )}

          {xmlData && (
            <div className="bg-white dark:bg-slate-800 p-4 rounded shadow-sm border border-gray-200 dark:border-gray-600 flex gap-2">
              <button
                onClick={saveXml}
                disabled={isLoading}
                className="bg-blue-800 text-white px-3 py-1 rounded hover:bg-blue-600 transition-colors"
              >
                Save
              </button>
              <button
                onClick={downloadXml}
                disabled={isLoading}
                className="bg-red-400 text-white px-3 py-1 rounded hover:bg-red-600 transition-colors"
              >
                Download
              </button>
              <button
                onClick={handlePreviewXml}
                disabled={isLoading}
                className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 transition-colors"
              >
                Preview XML
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}